import React, { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { isSanityBrand, isSanityRange } from "@util/types";
import { useCheckScreenWidth, useEventListener, useThrottle } from "@util/hooks";
import {
  ContainerWrapper,
  PanelWrapper,
  TextWrapper,
} from "./brandOrRangeDisplay.styles";

import { Maybe, SanityBrandOrRangeList, SanityImageWithMeta, SanityLink } from "@graphql-types";
import { Container, GradientOverlay } from "@util/standard";
import { Image, Link } from "@global";
import { isBrowser } from "@util/helper";

interface Props {
  data: Maybe<SanityBrandOrRangeList> | undefined;
  useH1Setup?: boolean;
  usePaginationOverride?: boolean;
}

interface DisplayPanelProps {
  image: Maybe<SanityImageWithMeta> | undefined;
  title: Maybe<string | undefined>;
  links: Maybe<Maybe<SanityLink>[]> | undefined;
  useH1Setup?: boolean;
}

function BrandOrRangeDisplay({ data, useH1Setup, usePaginationOverride }: Props) {
  if (data == null) return null;

  const containerRef = useRef<HTMLDivElement>(null);
  const [componentOnScreen, setComponentOnScreen] = useState(false);
  const { isMobileWidth, isTabletWidth } = useCheckScreenWidth();
  const throttledValue = useThrottle(
    componentOnScreen,
    isMobileWidth || isTabletWidth ? 1500 : 500,
  );

  const { brandsOrRange } = data;

  const handleComponentVisible = () => {
    if (!isBrowser()) return;
    if (
      containerRef.current &&
      window.pageYOffset > containerRef.current.offsetTop - 200 &&
      window.pageYOffset < containerRef.current.offsetTop + containerRef.current.offsetHeight
    ) {
      setComponentOnScreen(true);
      return;
    }
    setComponentOnScreen(false);
  };

  useEffect(() => {
    if (componentOnScreen && isBrowser() && containerRef.current) {
      scrollTo({ top: containerRef.current.offsetTop, behavior: "smooth" });
      return;
    }
  }, [throttledValue]);

  useEventListener("scroll", handleComponentVisible);

  const DisplayPanel = ({ image, title, links, useH1Setup }: DisplayPanelProps) => {
    return (
      <PanelWrapper>
        {image && <Image data={image} width="100%" isBackground />}
        <GradientOverlay gradient="lightOverlay" />
        <TextWrapper>
          {title && !useH1Setup && <h2>{title}</h2>}
          {title && useH1Setup && <h1>{title}</h1>}
          {links && (
            <Container tabletMargin="10px 0 0 0" flexWrap="wrap">
              {links.map((link, index) => {
                if (link == null) return null;

                const btnStyle = index === 0 && links.length > 1 ? "filledLight" : "outlineLight";
                return (
                  <Container
                    margin="50px 22px 0 0"
                    tabletMargin="0 7.5px 15px 7.5px"
                    key={link._key}
                  >
                    <Link
                      linkText={link.linkText}
                      internalLink={link.internalLink}
                      useIcon="iconRightArrow"
                      design={btnStyle}
                      buttonSize={!isMobileWidth ? "l" : "default"}
                    />
                    <Link
                      linkText={link.linkText}
                      externalLink={link.externalLink}
                      useIcon="iconRightArrow"
                      design={btnStyle}
                      buttonSize={!isMobileWidth ? "l" : "default"}
                    />
                  </Container>
                );
              })}
            </Container>
          )}
        </TextWrapper>
      </PanelWrapper>
    );
  };

  return (
    <>
      <ContainerWrapper width="100%" flexWrap="wrap" ref={containerRef}>
        {brandsOrRange && (
          brandsOrRange.map((item, index) => {
            if (item == null || item.rangeOrBrand == null) return null;

            const { rangeOrBrand, links } = item;

            if (isSanityBrand(rangeOrBrand)) {
              return (
                <DisplayPanel
                  key={rangeOrBrand.id}
                  title={rangeOrBrand?.title}
                  image={rangeOrBrand.coverImage}
                  links={links}
                  useH1Setup={useH1Setup && index === 0}
                />
              );
            }

            if (isSanityRange(rangeOrBrand)) {
              return (
                <DisplayPanel
                  key={rangeOrBrand.id}
                  title={rangeOrBrand?.title}
                  image={rangeOrBrand.imageWithMeta}
                  links={links}
                  useH1Setup={useH1Setup && index === 0}
                />
              );
            }

            return null;
          })
        )}
      </ContainerWrapper>
    </>
  );
}

export default BrandOrRangeDisplay;
